import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { 
  Flame, X, Send, Sparkles, Loader2, Lightbulb, TrendingUp,
  HelpCircle, Edit, Wand2, Copy, Video, Users, FolderOpen,
  Calendar, ChevronRight, MessageCircle, ArrowRight, Check
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Mode configuration
const MODES = {
  ask: {
    id: 'ask',
    name: 'Ask',
    description: 'Learn & get support',
    icon: HelpCircle,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
    borderColor: 'border-blue-500/30'
  },
  create: {
    id: 'create',
    name: 'Create',
    description: 'Generate & refine content',
    icon: Edit,
    color: 'text-primary',
    bgColor: 'bg-primary/20',
    borderColor: 'border-primary/30'
  },
  growth: {
    id: 'growth',
    name: 'Growth Coach',
    description: 'Strategy & performance',
    icon: TrendingUp,
    color: 'text-green-400',
    bgColor: 'bg-green-500/20',
    borderColor: 'border-green-500/30'
  }
};

// Icon mapping for actions
const ActionIcons = {
  video: Video,
  users: Users,
  folder: FolderOpen,
  calendar: Calendar,
  copy: Copy
};

const OracleAssistant = ({ studioContext = null }) => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState('ask');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [shortcuts, setShortcuts] = useState(null);
  const [showModeSelector, setShowModeSelector] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Determine current studio context based on route
  const getCurrentContext = () => {
    if (studioContext) return studioContext;
    
    const path = location.pathname;
    if (path.includes('/studio/video') || path.includes('/reels')) {
      return { studio: 'video', content_type: 'reel' };
    }
    if (path.includes('/studio/image') || path.includes('/images')) {
      return { studio: 'image', content_type: 'image' };
    }
    if (path.includes('/library')) {
      return { studio: 'library' };
    }
    if (path.includes('/scheduler')) {
      return { studio: 'scheduler' };
    }
    return null;
  };

  // Fetch shortcuts on mount
  useEffect(() => {
    const fetchShortcuts = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/api/delphi/shortcuts`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setShortcuts(response.data);
      } catch (error) {
        console.error('Failed to fetch shortcuts:', error);
      }
    };
    fetchShortcuts();
  }, [token]);

  // Scroll to bottom when messages update
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opening
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSend = async (messageText = input, overrideMode = null) => {
    if (!messageText.trim() || loading) return;

    const currentMode = overrideMode || mode;
    const userMessage = { role: 'user', content: messageText, mode: currentMode };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/api/delphi/ask`,
        { 
          message: messageText, 
          conversation_id: conversationId,
          mode: currentMode,
          context: getCurrentContext()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { response: aiResponse, conversation_id, suggestions: newSuggestions, actions } = response.data;
      
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: aiResponse, 
        mode: currentMode,
        actions: actions || []
      }]);
      setConversationId(conversation_id);
      if (newSuggestions?.length) setSuggestions(newSuggestions);
    } catch (error) {
      toast.error('Delphi encountered an issue. Please try again.');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleShortcutClick = (shortcut) => {
    if (shortcut.mode) setMode(shortcut.mode);
    handleSend(shortcut.prompt, shortcut.mode);
  };

  const handleActionClick = (action) => {
    if (action.type === 'navigate' && action.path) {
      navigate(action.path);
      setIsOpen(false);
    } else if (action.type === 'copy') {
      // Get the last assistant message content
      const lastMsg = [...messages].reverse().find(m => m.role === 'assistant');
      if (lastMsg) {
        navigator.clipboard.writeText(lastMsg.content);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast.success('Copied to clipboard!');
      }
    } else if (action.type === 'create_content') {
      navigate(action.path || '/studio/videos');
      setIsOpen(false);
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setConversationId(null);
    setSuggestions([]);
    setShowModeSelector(true);
  };

  const ModeIcon = MODES[mode]?.icon || HelpCircle;
  const modeConfig = MODES[mode];

  // Get contextual shortcuts based on current page
  const getContextualShortcuts = () => {
    if (!shortcuts?.contextual) return [];
    const ctx = getCurrentContext();
    if (!ctx?.studio) return [];
    return shortcuts.contextual[`${ctx.studio}_studio`] || shortcuts.contextual[ctx.studio] || [];
  };

  if (!token) return null;

  return (
    <>
      {/* Floating Assistant Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-primary to-orange-600 shadow-lg shadow-primary/30 flex items-center justify-center transition-all hover:scale-110 hover:shadow-xl hover:shadow-primary/40 ${isOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        data-testid="oracle-assistant-btn"
      >
        <Flame className="w-7 h-7 text-white" />
      </button>

      {/* Assistant Panel */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-[400px] max-w-[calc(100vw-48px)] h-[600px] max-h-[calc(100vh-100px)] bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-border bg-gradient-to-r from-primary/10 to-transparent">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-orange-600 flex items-center justify-center">
                  <Flame className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold">Ask Delphi</h3>
                  <p className="text-xs text-muted-foreground">Your AI guide</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {messages.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={startNewChat}
                    className="h-8 px-2 text-xs"
                  >
                    New
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsOpen(false)}
                  className="h-8 w-8"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Mode Selector */}
            <div className="flex gap-1">
              {Object.values(MODES).map((m) => {
                const Icon = m.icon;
                const isActive = mode === m.id;
                return (
                  <button
                    key={m.id}
                    onClick={() => setMode(m.id)}
                    className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      isActive 
                        ? `${m.bgColor} ${m.color} ${m.borderColor} border` 
                        : 'text-muted-foreground hover:bg-secondary/50'
                    }`}
                    data-testid={`mode-${m.id}`}
                  >
                    <Icon className="w-3 h-3 inline mr-1" />
                    {m.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Chat Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 ? (
              /* Empty State with Shortcuts */
              <div className="space-y-4">
                {/* Mode Description */}
                <div className={`p-3 rounded-xl ${modeConfig.bgColor} ${modeConfig.borderColor} border`}>
                  <div className="flex items-center gap-2 mb-1">
                    <ModeIcon className={`w-4 h-4 ${modeConfig.color}`} />
                    <span className={`font-medium text-sm ${modeConfig.color}`}>
                      {modeConfig.name} Mode
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {modeConfig.description}
                  </p>
                </div>

                {/* Primary Shortcuts */}
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground font-medium">Quick Actions</p>
                  <div className="grid grid-cols-2 gap-2">
                    {shortcuts?.primary?.map((shortcut) => (
                      <button
                        key={shortcut.id}
                        onClick={() => handleShortcutClick(shortcut)}
                        className="flex items-center gap-2 p-3 bg-secondary/30 hover:bg-secondary/50 rounded-xl text-sm transition-all text-left"
                        data-testid={`shortcut-${shortcut.id}`}
                      >
                        {shortcut.icon === 'sparkles' && <Sparkles className="w-4 h-4 text-primary" />}
                        {shortcut.icon === 'lightbulb' && <Lightbulb className="w-4 h-4 text-blue-400" />}
                        {shortcut.icon === 'wand' && <Wand2 className="w-4 h-4 text-purple-400" />}
                        {shortcut.icon === 'trending-up' && <TrendingUp className="w-4 h-4 text-green-400" />}
                        <span className="font-medium">{shortcut.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Contextual Shortcuts */}
                {getContextualShortcuts().length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground font-medium">
                      For this page
                    </p>
                    <div className="space-y-1">
                      {getContextualShortcuts().map((shortcut, i) => (
                        <button
                          key={i}
                          onClick={() => handleShortcutClick(shortcut)}
                          className="w-full flex items-center justify-between p-2 bg-secondary/20 hover:bg-secondary/40 rounded-lg text-sm transition-all"
                        >
                          <span>{shortcut.label}</span>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Getting Started Tip */}
                <div className="p-3 bg-muted/30 rounded-xl">
                  <p className="text-xs text-muted-foreground">
                    <Lightbulb className="w-3 h-3 inline mr-1" />
                    Tip: Ask me to create content, then say "make it shorter" or "adapt for TikTok" to refine it!
                  </p>
                </div>
              </div>
            ) : (
              /* Messages */
              <>
                {messages.map((message, i) => (
                  <div
                    key={i}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                      message.role === 'user' 
                        ? 'bg-primary text-primary-foreground rounded-br-md' 
                        : 'bg-secondary/50 rounded-bl-md'
                    }`}>
                      {message.role === 'assistant' && (
                        <div className="flex items-center gap-1.5 mb-2">
                          <Sparkles className={`w-3 h-3 ${MODES[message.mode]?.color || 'text-primary'}`} />
                          <span className={`text-xs font-medium ${MODES[message.mode]?.color || 'text-primary'}`}>
                            Delphi
                          </span>
                          <span className="text-xs text-muted-foreground">
                            • {MODES[message.mode]?.name || 'Ask'}
                          </span>
                        </div>
                      )}
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                      
                      {/* Action Buttons */}
                      {message.actions?.length > 0 && (
                        <div className="flex gap-2 mt-3 pt-2 border-t border-border/50">
                          {message.actions.map((action, j) => {
                            const ActionIcon = ActionIcons[action.icon] || ChevronRight;
                            return (
                              <Button
                                key={j}
                                size="sm"
                                variant="outline"
                                onClick={() => handleActionClick(action)}
                                className="h-7 text-xs"
                              >
                                <ActionIcon className="w-3 h-3 mr-1" />
                                {action.label}
                              </Button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-secondary/50 rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-primary" />
                        <span className="text-sm text-muted-foreground">Thinking...</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Follow-up Suggestions */}
          {messages.length > 0 && suggestions.length > 0 && !loading && (
            <div className="px-4 pb-2">
              <div className="flex gap-2 overflow-x-auto pb-1">
                {suggestions.map((suggestion, i) => (
                  <button
                    key={i}
                    onClick={() => handleSend(suggestion)}
                    className="shrink-0 px-3 py-1.5 bg-secondary/50 hover:bg-secondary rounded-full text-xs transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className="p-4 border-t border-border">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={
                  mode === 'create' ? "Describe what you want to create..." :
                  mode === 'growth' ? "Ask about growth strategies..." :
                  "Ask me anything..."
                }
                className="flex-1 bg-secondary/30 border-border"
                disabled={loading}
                data-testid="oracle-input"
              />
              <Button
                onClick={() => handleSend()}
                disabled={!input.trim() || loading}
                className="bg-primary hover:bg-primary/90"
                data-testid="oracle-send-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
            
            {/* Copy Last Response Button */}
            {messages.length > 0 && messages[messages.length - 1]?.role === 'assistant' && (
              <button
                onClick={() => handleActionClick({ type: 'copy' })}
                className="mt-2 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
              >
                {copied ? (
                  <>
                    <Check className="w-3 h-3 text-green-400" />
                    <span className="text-green-400">Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3 h-3" />
                    Copy response
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default OracleAssistant;
